import React from 'react';

import { Box } from '@opendoor/atoms/box';

import { AppleAuthRedirectButton } from '../../apple/AppleAuthRedirectButton';
import { useAnalyticsPrefix } from '../../AuthContext';
import { GoogleAuthRedirectButton } from '../../google/GoogleAuthRedirectButton';
import { RedirectAuthGroup } from '../../oauth/RedirectAuthButton';
import { useAccountForm } from '../AccountFormContext';
import { MethodBadgeAnchor } from './MethodBadgeAnchor';

/** Redirect-auth methods this module renders, in default stack order. */
export type SocialAuthMethod = 'google' | 'apple';

/**
 * COMPONENT segment of this module's `APP-COMPONENT-ACTION` analytics names.
 * The method stack belongs to the account form's entry surface, so it shares
 * `EmailModule`'s component rather than naming itself after the provider.
 */
const ANALYTICS_COMPONENT = 'account-form';

export interface SocialAuthModuleProps {
  /**
   * Per-method "last used" badge slot, anchored to the top-right corner of the
   * matching method button. GROWTH-1617 owns the pill and the last-used
   * lookup; this module only anchors whatever it returns.
   */
  renderLastUsedBadge?: (method: SocialAuthMethod) => React.ReactNode;
}

/**
 * The V2 social-auth stack: a full-width Google button (secondary/outline with
 * the brand icon) above a full-width Apple button (solid near-black), rendered
 * above the email field in the default composition. Both render
 * unconditionally: the whole V2 surface sits behind the `auth-fe-account-form`
 * flag, which only flips on once SIWA has shipped, so Apple needs no separate
 * SIWA gate here. Usable standalone under a bare `AccountFormProvider` or as
 * `AccountFormV2`'s social module.
 */
export const SocialAuthModule: React.FC<SocialAuthModuleProps> = ({ renderLastUsedBadge }) => {
  const { redirectTo, setFormError } = useAccountForm();
  const analyticsPrefix = useAnalyticsPrefix();
  const buttonAnalyticsPrefix = `${analyticsPrefix}-${ANALYTICS_COMPONENT}`;

  return (
    // A redirect that came back with `?auth_error=` is a failure of this form,
    // so its copy goes to the form's one alert region — which the composition
    // renders above the method stack — rather than inline under a button.
    <RedirectAuthGroup publishError={setFormError}>
      <Box className="flex w-full flex-col gap-4">
        <MethodBadgeAnchor badge={renderLastUsedBadge?.('google')}>
          <GoogleAuthRedirectButton
            analyticsPrefix={buttonAnalyticsPrefix}
            redirectTo={redirectTo}
            fullWidth
          />
        </MethodBadgeAnchor>
        <MethodBadgeAnchor badge={renderLastUsedBadge?.('apple')}>
          <AppleAuthRedirectButton
            analyticsPrefix={buttonAnalyticsPrefix}
            redirectTo={redirectTo}
            variant="secondaryInverse"
            fullWidth
          />
        </MethodBadgeAnchor>
      </Box>
    </RedirectAuthGroup>
  );
};
